import { API_BASE_URL, getBaseHeaders } from './api.config';
import { throwApiError } from './api.error';
import { Product, ProductDetail, UserProduct, PaginatedResponse } from './api.types';
import { authenticatedCall, optionallyAuthenticatedCall } from './auth';

export type ProductsResponse = PaginatedResponse<Product>;
export type UserProductsResponse = PaginatedResponse<UserProduct>;

export interface ProductSearchParams {
  page?: number;
  size?: number;
  sortField?: string;
  order?: 'ASC' | 'DESC';
  productType?: 'PRODUCT' | 'DEMAND';
  name?: string | null;
  location?: {
    ids?: string[];
    range?: {
      range?: number;
    };
  };
  categoryId?: string | null;
  subCategoryIds?: string[];
  minPrice?: number | null;
  maxPrice?: number | null;
  negotiationType?: 'NEGOTIABLE' | 'NON_NEGOTIABLE' | null;
  username?: string | null;
}

export interface CreateProductRequest {
  title: string;
  description: string;
  price: {
    price: number;
    currency: string;
    quantity: number;
    unitType: string;
  };
  categoryId: string;
  subCategoryId?: string;
  negotiable: boolean;
  locationId: string;
  productType: 'PRODUCT' | 'DEMAND';
}

export interface CreateProductResponse {
  id: string;
}

export interface UpdateProductRequest {
  title: string;
  description: string;
  price: {
    price: number;
    currency: string;
    quantity: number;
    unitType: string;
  };
  categoryId: string;
  subCategoryId?: string;
  locationId: string;
  negotiable: boolean;
  productType: 'PRODUCT' | 'DEMAND';
}

export interface ProductImageUpdateRequest {
  retainedImageIds: string[];
  favoriteExistingImageId: string | null;
  favoriteNewImageIndex: number | null;
}

const productSearchCall = (url: string, options: RequestInit): Promise<Response> => {
  return optionallyAuthenticatedCall(url, options);
};

export const fetchProducts = async (
  page: number = 1,
  size: number = 5,
  productType: 'PRODUCT' | 'DEMAND' = 'PRODUCT',
  name: string | null = null,
  locationIds: string[] = [],
  userId: string | null = null,
  sortField: string = 'PUBLISHED_AT',
  order: 'ASC' | 'DESC' = 'DESC',
  excludedIds: string[] = []
): Promise<ProductsResponse> => {
  const requestBody: any = {
    ids: [],
    status: 'ACTIVE',
    productType: productType,
    name: name,
    userId: userId,
    excludedIds: excludedIds
  };

  if (locationIds && locationIds.length > 0) {
    requestBody.location = {
      ids: locationIds
    };
  }

  const response = await productSearchCall(
    `${API_BASE_URL}/products/search?page=${page}&size=${size}&sortField=${sortField}&order=${order}`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const searchProducts = async (params: ProductSearchParams): Promise<ProductsResponse> => {
  const {
    page = 1,
    size = 20,
    sortField = 'PUBLISHED_AT',
    order = 'DESC',
    productType = 'PRODUCT',
    name = null,
    location,
    categoryId = null,
    subCategoryIds = [],
    minPrice = null,
    maxPrice = null,
    negotiationType = null,
    username = null
  } = params;

  const requestBody: any = {
    ids: [],
    status: 'ACTIVE',
    productType: productType,
    name: name,
    categoryId: categoryId,
    subCategoryIds: subCategoryIds,
    minPrice: minPrice,
    maxPrice: maxPrice,
    negotiationType: negotiationType,
    username: username
  };

  if (location) {
    requestBody.location = location;
  }

  const response = await productSearchCall(
    `${API_BASE_URL}/products/search?page=${page}&size=${size}&sortField=${sortField}&order=${order}`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const fetchProductDetail = async (productId: string): Promise<ProductDetail> => {
  const url = `${API_BASE_URL}/products/${productId}`;
  const options: RequestInit = {
    method: 'GET',
    headers: getBaseHeaders()
  };
  const response = await optionallyAuthenticatedCall(url, options);

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const fetchOwnProductDetail = async (productId: string): Promise<ProductDetail> => {
  const response = await authenticatedCall(`${API_BASE_URL}/products/me/${productId}`, {
    method: 'GET',
    headers: getBaseHeaders()
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const fetchUserProducts = async (
  productType: 'PRODUCT' | 'DEMAND' = 'PRODUCT',
  page: number = 1,
  size: number = 10,
  status: string | null = null,
  name: string | null = null,
  locationId: string | null = null
): Promise<UserProductsResponse> => {
  const body: any = {
    productType: productType
  };

  if (status) {
    body.status = status;
  }

  if (name?.trim()) {
    body.name = name.trim();
  }

  if (locationId) {
    body.location = {
      ids: [locationId]
    };
  }

  const response = await authenticatedCall(
    `${API_BASE_URL}/products/me/search?extraFields=MESSAGE_COUNT&page=${page}&size=${size}&sortField=PUBLISHED_AT&order=DESC`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const createProduct = async (
  data: CreateProductRequest,
  images: File[] = []
): Promise<CreateProductResponse> => {
  const formData = new FormData();
  formData.append(
    'product',
    new Blob([JSON.stringify(data)], { type: 'application/json' }),
    'product.json'
  );
  images.forEach((image) => formData.append('images', image));

  const response = await authenticatedCall(
    `${API_BASE_URL}/products`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders()
      },
      body: formData
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const updateProduct = async (
  productId: string,
  data: UpdateProductRequest,
  imageUpdate: ProductImageUpdateRequest,
  images: File[] = []
): Promise<void> => {
  const formData = new FormData();
  formData.append(
    'product',
    new Blob([JSON.stringify(data)], { type: 'application/json' }),
    'product.json'
  );
  formData.append(
    'imageUpdate',
    new Blob([JSON.stringify(imageUpdate)], { type: 'application/json' }),
    'image-update.json'
  );
  images.forEach((image) => formData.append('images', image));

  const response = await authenticatedCall(
    `${API_BASE_URL}/products/${productId}`,
    {
      method: 'PUT',
      headers: {
        ...getBaseHeaders()
      },
      body: formData
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const rateProduct = async (
  productId: string,
  rate: number
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/products/${productId}/rate`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ rate })
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const reportProduct = async (
  productId: string,
  reasons: string[]
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/products/${productId}/report`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ reasons })
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const updateProductStatus = async (
  productId: string,
  status: 'ACTIVE' | 'EXPIRED'
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/products/${productId}/status`,
    {
      method: 'PUT',
      headers: {
        'accept': '*/*',
        'ROADE-API-TIME-ZONE': 'Europe/Bucharest',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ status })
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const deleteProduct = async (productId: string): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/products/${productId}`,
    {
      method: 'DELETE',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const incrementProductView = async (productId: string): Promise<void> => {
  try {
    await fetch(
      `${API_BASE_URL}/products/${productId}/view`,
      {
        method: 'POST',
        headers: {
        ...getBaseHeaders()
      },
        body: ''
      }
    );
  } catch (error) {
    console.error('Error incrementing product view:', error);
  }
};

export const fetchFavoriteProducts = async (
  page: number = 1,
  size: number = 10,
  productType: 'PRODUCT' | 'DEMAND' | null = null
): Promise<ProductsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString()
  });
  if (productType) {
    params.set('productType', productType);
  }

  const response = await authenticatedCall(
    `${API_BASE_URL}/products/favorites?${params.toString()}`,
    {
      method: 'GET',
      headers: getBaseHeaders()
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const addProductFavorite = async (productId: string): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/products/${productId}/favorite`,
    {
      method: 'POST',
      headers: getBaseHeaders()
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const removeProductFavorite = async (productId: string): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/products/${productId}/favorite`,
    {
      method: 'DELETE',
      headers: getBaseHeaders()
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};
